import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Breadcrumbs,
  Link,
  Switch,
  FormControlLabel
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, CloudUpload } from '@mui/icons-material';
import documentsService from '../services/documentsService';
import { projectsService, Project } from '../services/projectsService';
import api from '../services/api';

const DocumentUpload: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'contract' | 'blueprint' | 'permit' | 'invoice' | 'report' | 'photo' | 'other'>('other');
  const [category, setCategory] = useState('General');
  const [projectId, setProjectId] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [companies, setCompanies] = useState<{ _id: string; name: string }[]>([]);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        // Load projects for the Project select
        const resp = await projectsService.getAll();
        const items = (resp as any)?.data?.data || (resp as any)?.data?.projects || [];
        setProjects(Array.isArray(items) ? items : []);

        // Load current user to determine role and company
        const me = await api.get('/auth/me');
        const user = me?.data?.user || me?.data;
        const role = user?.role ? String(user.role) : null;
        setUserRole(role);

        const company = user?.company;
        const id = typeof company === 'object' ? (company?._id || company?.id) : company;
        if (id) {
          const cid = String(id);
          setCompanyId(cid);
          // For non-admin, auto bind selection
          if (role && role.toLowerCase() !== 'admin') {
            setSelectedCompanyId(cid);
          }
        }

        // Admins can pick a company
        if (role && role.toLowerCase() === 'admin') {
          try {
            const list = await api.get('/companies');
            const data = list?.data?.data ?? list?.data ?? [];
            const mapped = Array.isArray(data)
              ? data.map((c: any) => ({ _id: String(c._id || c.id), name: String(c.name || '') }))
              : [];
            setCompanies(mapped);
          } catch (err) {
            console.warn('Failed to load companies for admin selection');
          }
        }
      } catch (err) {
        console.warn('Failed to bootstrap upload form', err);
      }
    };
    bootstrap();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) {
      setError('Please select a file to upload');
      return;
    }
    // Require company for admin if none selected
    if ((userRole || '').toLowerCase() === 'admin' && !selectedCompanyId) {
      setError('Please select a company to upload under');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const tags = tagsInput
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);
      await documentsService.upload({
        file,
        name: title,
        type,
        category,
        project: projectId || undefined,
        tags,
        description,
        isPublic,
        company: selectedCompanyId || companyId || undefined,
      });
      navigate('/documents');
    } catch (err: any) {
      // Surface backend validation details if available
      const respData = err?.response?.data;
      const baseMessage = respData?.message || err?.message || 'Failed to upload document';
      const errors = respData?.errors;
      let details = '';
      if (Array.isArray(errors) && errors.length > 0) {
        const mapped = errors.map((item: any) => {
          if (typeof item === 'string') return item;
          if (item?.msg) return item.msg;
          if (item?.message) return item.message;
          if (item?.error) return item.error;
          return JSON.stringify(item);
        });
        details = mapped.join('; ');
      }
      setError(details ? `${baseMessage}: ${details}` : baseMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/documents')}
          sx={{ textDecoration: 'none' }}
        >
          Documents
        </Link>
        <Typography color="text.primary">Upload</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/documents')} sx={{ mr: 2 }}>
          Back to Documents
        </Button>
        <Typography variant="h4" component="h1">
          Upload Document
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Button variant="outlined" component="label">
                  Select File
                  <input
                    type="file"
                    hidden
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </Button>
                <Typography sx={{ mt: 1 }}>
                  {file ? file.name : 'No file selected'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Type</InputLabel>
                  <Select value={type} onChange={(e) => setType(e.target.value as any)} label="Type">
                    {['contract','blueprint','permit','invoice','report','photo','other'].map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {(userRole || '').toLowerCase() === 'admin' && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Company</InputLabel>
                    <Select
                      value={selectedCompanyId}
                      onChange={(e) => setSelectedCompanyId(String(e.target.value))}
                      label="Company"
                    >
                      <MenuItem value="">Select a company</MenuItem>
                      {companies.map((c) => (
                        <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Project</InputLabel>
                  <Select
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    label="Project"
                  >
                    <MenuItem value="">None</MenuItem>
                    {projects.map((p) => (
                      <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Tags (comma separated)"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="contract, client, Q4"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
                  label="Public"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button type="submit" variant="contained" startIcon={<CloudUpload />} disabled={loading}>
                {loading ? 'Uploading...' : 'Upload'}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/documents')} disabled={loading}>
                Cancel
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DocumentUpload;